import { Fragment, useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon from '../common/Icon';
import CloseButton from '../common/CloseButton';
import PartOptions from '../common/PartOptions';
import PromptModal from '../modals/PromptModal';
import ChordBarsChart from '../ChordBarsChart';
import theme from '../../utils/theme';
import { createSortable, destroySortable } from '../../utils/sorting';
import { isHiddenInOverview } from '../../utils/parts';
import { getPartGrid, isChartDrawn } from '../../utils/partCharts';
import { getBeatsPerBar } from '../../utils/songTiming';
import { computePartNumbers, isSectionPart, isPartInSection } from '../../utils/partNumbers';

import { ReactComponent as MusicIcon } from '../../icons/album-list.svg';
import { ReactComponent as XMarkIcon } from '../../icons/xmark.svg';
import { ReactComponent as CheckIcon } from '../../icons/check-circle-solid.svg';
import { ReactComponent as RepeatIcon } from '../../icons/repeat.svg';
import { ReactComponent as AssignedIcon } from '../../icons/user.svg';

import './SongPartsGridModal.scss';

// An instrumental's chart draws under its words, as it does in View
const drawnChart = (part, beatsPerBar) => (isChartDrawn(part) ? getPartGrid(part, beatsPerBar) : null);

const renderPartLyrics = (part, beatsPerBar) => {
  const chart = drawnChart(part, beatsPerBar);

  return (
    <>
      {part.lyrics}
      {chart && (
        <ChordBarsChart
          lines={chart.lines}
          sections={chart.sections}
          beatsPerBar={beatsPerBar}
          countInEntry={chart.countInEntry}
        />
      )}
    </>
  );
};

function SongPartsGridModal({
  isOpen,
  onClose,
  parts,
  onPartDelete,
  onPartDuplicate,
  onPartEdit,
  onPartUpdate,
  onPartSort,
  editPartId,
  overview,
  timeSignature,
}) {
  const { t } = useTranslation();
  const beatsPerBar = getBeatsPerBar(timeSignature);
  const [editingPartId, setEditingPartId] = useState(null);
  const [editingLyrics, setEditingLyrics] = useState('');
  const [gridRef, setGridRef] = useState(null);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [pendingPart, setPendingPart] = useState(null);

  useEffect(() => {
    // Overview hides duplicates/notes, so the visible order is a subset — drag-and-drop
    // would reorder against a partial list, so it's disabled while filtering.
    // Also disabled while a part is being edited (inline here, or in the main form's
    // lyrics editor), since reordering mid-edit could change which part it writes to.
    if (gridRef && onPartSort && !overview && !editPartId && !editingPartId) {
      createSortable(gridRef, onPartSort, {
        // Longer, eased settle so cards visibly glide into their new slots
        animation: 220,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        // Render a styleable clone that follows the cursor (the native HTML5 drag image
        // can't be styled and gets janky inside a CSS grid). Kept inside the grid (not on
        // <body>) so the clone inherits its per-type card color from the grid-scoped mixin.
        forceFallback: true,
        fallbackTolerance: 4,
        // The lifted clone that follows the cursor
        fallbackClass: 'song-part-grid-item--floating',
        // The card left behind in the grid, showing where it will land
        ghostClass: 'song-part-grid-item--ghost',
        // The original source card
        chosenClass: 'song-part-grid-item--chosen',
      });
    }
    return () => destroySortable();
  }, [gridRef, onPartSort, overview, editPartId, editingPartId]);

  if (!isOpen) return null;

  const handlePartEdit = (part) => {
    onPartEdit(part);
    onClose();
  };

  const handleStartInlineEdit = (part) => {
    // If another part is being edited, check if there are actual changes
    if (editingPartId && editingPartId !== part.id) {
      const currentPart = parts.find((p) => p.id === editingPartId);
      const hasChanges = currentPart && editingLyrics !== currentPart.lyrics;

      if (hasChanges) {
        // Show confirmation modal only if there are unsaved changes
        setPendingPart(part);
        setShowSavePrompt(true);
        return;
      }

      // No changes, just switch directly
      setEditingPartId(null);
      setEditingLyrics('');
    }

    setEditingPartId(part.id);
    setEditingLyrics(part.lyrics);
  };

  const handleSaveInlineEdit = (part) => {
    const trimmedLyrics = editingLyrics.trim();
    if (onPartUpdate && trimmedLyrics !== part.lyrics && trimmedLyrics !== '') {
      onPartUpdate(part.id, editingLyrics);
    }
    setEditingPartId(null);
    setEditingLyrics('');
  };

  const handleCancelInlineEdit = () => {
    setEditingPartId(null);
    setEditingLyrics('');
  };

  const handleSaveAndSwitch = () => {
    // Save current changes
    if (editingPartId && onPartUpdate) {
      const currentPart = parts.find((p) => p.id === editingPartId);
      const trimmedLyrics = editingLyrics.trim();
      if (currentPart && trimmedLyrics !== currentPart.lyrics && trimmedLyrics !== '') {
        onPartUpdate(currentPart.id, editingLyrics);
      }
    }

    // Switch to pending part
    if (pendingPart) {
      setEditingPartId(pendingPart.id);
      setEditingLyrics(pendingPart.lyrics);
      setPendingPart(null);
    }
    setShowSavePrompt(false);
  };

  const handleDiscardAndSwitch = () => {
    // Discard changes by clearing editing state without saving
    const nextPart = pendingPart;

    // Clear current editing state
    setEditingPartId(null);
    setEditingLyrics('');
    setPendingPart(null);
    setShowSavePrompt(false);

    // Switch to pending part
    if (nextPart) {
      setEditingPartId(nextPart.id);
      setEditingLyrics(nextPart.lyrics);
    }
  };

  const handleCancelSwitch = () => {
    // Cancel the switch, keep current part active
    setPendingPart(null);
    setShowSavePrompt(false);
  };

  const handleLyricsChange = (e) => {
    setEditingLyrics(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancelInlineEdit();
    }
  };

  const gridClasses = cx('song-parts-grid', { overview, editing: !!(editPartId || editingPartId) });
  const partNumbers = computePartNumbers(parts);

  return (
    <>
      <PromptModal
        isOpen={showSavePrompt}
        onClose={handleCancelSwitch}
        primaryText={t('songForm.grid.dontSave')}
        secondaryText={t('songForm.grid.saveAndSwitch')}
        message={t('songForm.grid.unsaved')}
        onPrimaryAction={handleDiscardAndSwitch}
        onSecondaryAction={handleSaveAndSwitch}
        autoCloseOnPrimary={false}
        isDestructive={true}
      />

      <div className="song-parts-grid-modal-overlay" onClick={onClose}>
        <div className="song-parts-grid-modal" onClick={(e) => e.stopPropagation()}>
          <div className="song-parts-grid-modal__header">
            <div>
              <h3>{t('songForm.grid.title')}</h3>
            </div>
            <CloseButton onClick={onClose} />
          </div>

          <div className="song-parts-grid-modal__content">
            {parts.length === 0 ? (
              <div className="song-parts-grid-modal__empty">
                <p>{t('songForm.grid.noParts')}</p>
              </div>
            ) : (
              <div className={gridClasses} ref={(ref) => setGridRef(ref)}>
                {parts.map((part, partIndex) => (
                  <SongPartGridItem
                    key={part.id}
                    part={part}
                    parts={parts}
                    partIndex={partIndex}
                    displayNumber={partNumbers[part.id]}
                    isCollapsed={overview && isHiddenInOverview(part)}
                    onPartDelete={onPartDelete}
                    onPartDuplicate={onPartDuplicate}
                    onPartEdit={handlePartEdit}
                    editPartId={editPartId}
                    editingPartId={editingPartId}
                    editingLyrics={editingLyrics}
                    onStartInlineEdit={handleStartInlineEdit}
                    onSaveInlineEdit={handleSaveInlineEdit}
                    onCancelInlineEdit={handleCancelInlineEdit}
                    onLyricsChange={handleLyricsChange}
                    onKeyDown={handleKeyDown}
                    beatsPerBar={beatsPerBar}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function SongPartGridItem({
  part,
  parts,
  partIndex,
  displayNumber = part.number,
  isCollapsed,
  onPartDelete,
  onPartDuplicate,
  onPartEdit,
  editPartId,
  editingPartId,
  editingLyrics,
  onStartInlineEdit,
  onSaveInlineEdit,
  onCancelInlineEdit,
  onLyricsChange,
  onKeyDown,
  beatsPerBar,
}) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [lockedHeight, setLockedHeight] = useState(null);

  const isOtherPartBeingEdited = editPartId && editPartId !== part.id;
  const isInlineEditing = editingPartId === part.id;
  const isLyricsEmpty = editingLyrics.trim() === '';
  const partName = t(`parts.${part.name}`, { defaultValue: part.name });

  // Entering edit mode swaps the lyrics <pre> (sized to its actual content) for a
  // textarea that only contributes its min-height to layout, which can shrink the
  // grid row if this card was the tallest one in it. Lock in the pre-edit height so
  // the row doesn't collapse.
  useEffect(() => {
    if (!isInlineEditing) {
      setLockedHeight(null);
    }
  }, [isInlineEditing]);

  const handleLyricsClick = () => {
    if (cardRef.current) {
      setLockedHeight(cardRef.current.getBoundingClientRect().height);
    }
    onStartInlineEdit(part);
  };

  // Overview hides duplicates/notes, but the item stays mounted (just display:none) so the
  // Sortable child indices still line up with the full parts array that handlePartSort splices.
  const partClasses = `song-part-grid-item ${isOtherPartBeingEdited ? 'dimmed' : ''} ${
    isInlineEditing ? 'editing' : ''
  } ${isCollapsed ? 'song-part-grid-item--hidden' : ''}`;

  return (
    <div
      ref={cardRef}
      className={theme.renderPartColor(part.name, partClasses)}
      style={isInlineEditing && lockedHeight ? { minHeight: lockedHeight } : undefined}
    >
      <div className="song-part-grid-item__header">
        <div className="song-part-grid-item__title">
          <Icon icon={<MusicIcon />} className="drag-handle" />
          <span className="song-part-grid-item__name">
            {isSectionPart(part) ? (
              partName
            ) : (
              <>
                {partName} {displayNumber}
                {part.repeatCount > 1 && <span className="repeat-badge">×{part.repeatCount}</span>}
                {part.isDuplicate && (
                  <RepeatIcon className="duplicate-icon" aria-label={t('songForm.part.isDuplicate')} />
                )}
                {part.assignedUsers?.length > 0 && (
                  <AssignedIcon className="assigned-icon" aria-label={t('songForm.part.hasAssigned')} />
                )}
              </>
            )}
          </span>
        </div>

        {!editPartId && (
          <div className="song-part-grid-item__controls">
            {isInlineEditing ? (
              <>
                <button
                  onClick={() => onSaveInlineEdit(part)}
                  title={t('songForm.grid.saveChanges')}
                  disabled={isLyricsEmpty}
                >
                  <Icon icon={<CheckIcon />} className="save-part solid" />
                </button>
                <button onClick={onCancelInlineEdit} title={t('songForm.grid.cancelEditing')}>
                  <Icon icon={<XMarkIcon />} className="cancel-edit" />
                </button>
              </>
            ) : (
              <PartOptions
                part={part}
                partIndex={partIndex}
                onPartDuplicate={onPartDuplicate}
                onPartEdit={onPartEdit}
                onPartDelete={onPartDelete}
                showDuplicateToSectionEnd={!isSectionPart(part) && isPartInSection(parts, part.id)}
              />
            )}
          </div>
        )}
      </div>

      <div className="song-part-grid-item__lyrics">
        {isInlineEditing ? (
          <textarea
            value={editingLyrics}
            onChange={onLyricsChange}
            onKeyDown={onKeyDown}
            className="song-part-grid-item__lyrics-editor"
            autoFocus
            placeholder={t('songForm.grid.lyricsPlaceholder')}
          />
        ) : (
          <pre
            className={!editPartId ? 'clickable' : ''}
            onClick={!editPartId ? handleLyricsClick : undefined}
            title={!editPartId ? t('songForm.grid.clickToEdit') : ''}
          >
            {part.lyrics || drawnChart(part, beatsPerBar) ? (
              renderPartLyrics(part, beatsPerBar)
            ) : (
              <span className="no-lyrics-placeholder">{t('songForm.grid.clickToAdd')}</span>
            )}
          </pre>
        )}
      </div>
    </div>
  );
}

export default SongPartsGridModal;
